import { z } from 'zod';
import { isValidPlatformPlacement, PREFLIGHT_PLACEMENTS, PREFLIGHT_PLATFORMS } from '../constants/preflight-criteria.js';
import { UploadIdSchema } from '../constants/upload-id.js';

/** PRD §10.3 — at most 25 assets (files + URLs) per report. */
export const MAX_ASSETS_PER_REPORT = 25;

const HttpsUrlSchema = z
  .string()
  .trim()
  .url('Each URL must be a valid URL')
  .refine((u) => u.startsWith('https://'), 'Only HTTPS URLs are accepted');

export const CreateReportBodySchema = z
  .object({
    uploadIds: z
      .array(UploadIdSchema)
      .default([])
      .refine((ids) => new Set(ids).size === ids.length, 'Duplicate upload IDs are not allowed'),
    urls: z.array(HttpsUrlSchema).default([]),
    platform: z.enum(PREFLIGHT_PLATFORMS),
    placement: z.enum(PREFLIGHT_PLACEMENTS),
    brandName: z.string().trim().min(1, 'Brand name is required').max(255),
    marketIds: z
      .array(z.string().cuid2('A market is required'))
      .min(1, 'At least one market is required')
      .refine((ids) => new Set(ids).size === ids.length, 'Duplicate market IDs are not allowed'),
    name: z.string().trim().min(1).max(255).optional(),
  })
  .refine(
    (b) => b.uploadIds.length + b.urls.length > 0,
    'At least one upload or URL is required',
  )
  .refine(
    (b) => b.uploadIds.length + b.urls.length <= MAX_ASSETS_PER_REPORT,
    `A report may contain at most ${MAX_ASSETS_PER_REPORT} assets`,
  )
  .refine(
    (b) => isValidPlatformPlacement(b.platform, b.placement),
    'Placement is not valid for the selected platform',
  );

export type CreateReportBody = z.infer<typeof CreateReportBodySchema>;
